import {
  Button,
  TextField,
  Autocomplete,
  useSnackbar,
  Loader,
  Option,
} from '@fdha/web-ui-library';
import { Box, Grid, Stack, styled, Typography, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { GraphQLError } from 'graphql';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { groupBy } from 'lodash';
import {
  Dish,
  MealKit,
  Menu,
  MenuInput,
  useAddMenuMutation,
  useListDishesQuery,
  useListMealKitsQuery,
  useUpdateMenuMutation,
} from '@fdha/graphql-api-foodops';
import { addAtLeastOneKeyValidation } from '@fdha/common-utils';

const NUMBER_OF_RESULTS = 10000;

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required(),
  entrees: addAtLeastOneKeyValidation({ systemType: 'web' }),
  snacksAndSides: addAtLeastOneKeyValidation({ systemType: 'web' }),
});

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

interface MenuSchema {
  name: string;
  entrees: {
    breakfast: Option[];
    lunch: Option[];
    dinner: Option[];
  };
  snacksAndSides: {
    snacks: Option[];
    sides: Option[];
  };
  mealKits: {
    breakfast: Option[];
    lunch: Option[];
    dinner: Option[];
  };
}

interface AddOrEditMenuProps {
  data?: Menu;
  onSuccess?: () => void;
  onFinish?: () => void;
}

const AddOrEditMenu: React.FC<AddOrEditMenuProps> = ({
  data,
  onSuccess,
  onFinish,
}) => {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const preparedMealChipSx = {
    '& .MuiChip-filled': {
      background: theme.palette.success.lightBg,
      border: `1px solid ${theme.palette.success.border}`,
    },
    '& .MuiChip-root': {
      background: theme.palette.success.lightBg,
      border: `1px solid ${theme.palette.success.border}`,
    },
  };

  const mealKitChipSx = {
    '& .MuiChip-filled': {
      background: theme.palette.warning.lightBg,
      border: `1px solid ${theme.palette.warning.border}`,
    },
    '& .MuiChip-root': {
      background: theme.palette.warning.lightBg,
      border: `1px solid ${theme.palette.warning.border}`,
    },
  };

  const [addMenu] = useAddMenuMutation();
  const [updateMenu] = useUpdateMenuMutation();

  const { data: dishesData, loading: loadingDishes } = useListDishesQuery({
    variables: { first: NUMBER_OF_RESULTS },
    fetchPolicy: 'cache-and-network',
  });

  const { data: mealKitsData, loading: loadingMealKits } = useListMealKitsQuery(
    {
      variables: { first: NUMBER_OF_RESULTS },
      fetchPolicy: 'cache-and-network',
    }
  );

  const getDishesOptions = (dishes: Dish[]) =>
    dishes?.map((dish: Dish) => ({
      label: dish.name,
      id: dish.id,
    })) ?? [];

  const getMealKitOptions = (mealKits: MealKit[]) =>
    mealKits?.map((mealKit: MealKit) => ({
      label: mealKit.name,
      id: mealKit.id,
    })) ?? [];

  const dishesNodes = dishesData?.dishes.edges.map((edge) => edge.node);
  const dishesGroupByType = dishesNodes ? groupBy(dishesNodes, 'type') : {};
  const mealDishes = dishesGroupByType?.meal;
  const snackDishes = dishesGroupByType?.snack;
  const isLoading = loadingDishes || loadingMealKits;

  const mealKitNodes = mealKitsData?.listMealKits.edges.map(
    (edge) => edge.node
  );
  const mealKitOptions = useMemo(
    () => getMealKitOptions(mealKitNodes || []),
    [mealKitNodes]
  );

  const { mealDishesOptions, snackDishesOptions } = useMemo(() => {
    return {
      mealDishesOptions: getDishesOptions(mealDishes),
      snackDishesOptions: getDishesOptions(snackDishes),
    };
  }, [mealDishes, snackDishes]);

  const initialValues: MenuSchema = {
    name: data?.name || '',
    entrees: {
      breakfast:
        mealDishesOptions.filter((opt) => data?.breakfast.includes(opt.id)) ||
        [],
      lunch:
        mealDishesOptions.filter((opt) => data?.lunch.includes(opt.id)) || [],
      dinner:
        mealDishesOptions.filter((opt) => data?.dinner.includes(opt.id)) || [],
    },
    snacksAndSides: {
      snacks:
        snackDishesOptions.filter((opt) => data?.snacks.includes(opt.id)) || [],
      sides:
        snackDishesOptions.filter((opt) => data?.sides.includes(opt.id)) || [],
    },
    mealKits: {
      breakfast:
        mealKitOptions.filter((opt) =>
          data?.breakfastMealKits?.includes(opt.id)
        ) || [],
      lunch:
        mealKitOptions.filter((opt) => data?.lunchMealKits?.includes(opt.id)) ||
        [],
      dinner:
        mealKitOptions.filter((opt) =>
          data?.dinnerMealKits?.includes(opt.id)
        ) || [],
    },
  };

  const handleSubmit = async (values: MenuSchema) => {
    const { breakfast, lunch, dinner } = values.entrees;
    const { snacks, sides } = values.snacksAndSides;
    const {
      breakfast: breakfastMealKits,
      lunch: lunchMealKits,
      dinner: dinnerMealKits,
    } = values.mealKits;

    const payload: MenuInput = {
      name: values.name,
      breakfast: breakfast.map((b) => b.id),
      lunch: lunch.map((l) => l.id),
      dinner: dinner.map((d) => d.id),
      snacks: snacks.map((s) => s.id),
      sides: sides.map((sd) => sd.id),
      breakfastMealKits: breakfastMealKits.map((b) => b.id),
      lunchMealKits: lunchMealKits.map((l) => l.id),
      dinnerMealKits: dinnerMealKits.map((d) => d.id),
    };

    try {
      if (data) {
        await updateMenu({ variables: { menuId: data.id, input: payload } });
      } else {
        await addMenu({ variables: { input: payload } });
      }

      const action = data ? 'updated' : 'created';
      const message = `Menu ${action} with success`;

      showSnackbar({
        message,
        severity: 'success',
      });
      onSuccess && onSuccess();
    } catch (e: any) {
      const handledError = e.graphQLErrors.find(
        (error: GraphQLError) => error.extensions?.code === 'BAD_USER_INPUT'
      );
      const message = handledError?.message || 'Error to create menu';

      showSnackbar({
        message,
        severity: 'error',
      });
    } finally {
      onFinish && onFinish();
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Create Menu</Typography>
      {isLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initialValues}
          validateOnMount
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, isSubmitting, isValid, setFieldValue, values }) => {
            return (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      title="Name"
                      name="name"
                      onChange={handleChange}
                      value={values.name}
                    />
                  </Grid>

                  {/* Prepared meal options section */}
                  <Grid item xs={12}>
                    <SectionContainer>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Prepared meal options
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Breakfast"
                            options={mealDishesOptions}
                            defaultValue={[]}
                            value={values.entrees.breakfast}
                            loading={loadingDishes}
                            onChange={(_event, value) => {
                              setFieldValue('entrees.breakfast', value);
                            }}
                            sx={preparedMealChipSx}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Lunch"
                            options={mealDishesOptions}
                            defaultValue={[]}
                            value={values.entrees.lunch}
                            loading={loadingDishes}
                            onChange={(_event, value) => {
                              setFieldValue('entrees.lunch', value);
                            }}
                            sx={preparedMealChipSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Dinner"
                            options={mealDishesOptions}
                            defaultValue={[]}
                            value={values.entrees.dinner}
                            loading={loadingDishes}
                            onChange={(_event, value) => {
                              setFieldValue('entrees.dinner', value);
                            }}
                            sx={preparedMealChipSx}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Snacks"
                            options={snackDishesOptions}
                            defaultValue={[]}
                            value={values.snacksAndSides.snacks}
                            loading={loadingDishes}
                            onChange={(_event, value) => {
                              setFieldValue('snacksAndSides.snacks', value);
                            }}
                            sx={preparedMealChipSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Sides"
                            options={snackDishesOptions}
                            defaultValue={[]}
                            value={values.snacksAndSides.sides}
                            loading={loadingDishes}
                            onChange={(_event, value) => {
                              setFieldValue('snacksAndSides.sides', value);
                            }}
                            sx={preparedMealChipSx}
                          />
                        </Grid>
                      </Grid>
                    </SectionContainer>
                  </Grid>

                  {/* Meals kit options section - only show if at least one option is selected */}

                  <Grid item xs={12}>
                    <SectionContainer>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Meals kit options
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Breakfast"
                            options={mealKitOptions}
                            defaultValue={[]}
                            value={values.mealKits.breakfast}
                            loading={loadingMealKits}
                            onChange={(_event, value) => {
                              setFieldValue('mealKits.breakfast', value);
                            }}
                            sx={mealKitChipSx}
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Lunch"
                            options={mealKitOptions}
                            defaultValue={[]}
                            value={values.mealKits.lunch}
                            loading={loadingMealKits}
                            onChange={(_event, value) => {
                              setFieldValue('mealKits.lunch', value);
                            }}
                            sx={mealKitChipSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            allowSelectAll
                            title="Dinner"
                            options={mealKitOptions}
                            defaultValue={[]}
                            value={values.mealKits.dinner}
                            loading={loadingMealKits}
                            onChange={(_event, value) => {
                              setFieldValue('mealKits.dinner', value);
                            }}
                            sx={mealKitChipSx}
                          />
                        </Grid>
                      </Grid>
                    </SectionContainer>
                  </Grid>
                </Grid>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  columnGap={1}
                  marginTop={3}
                >
                  <Button color="primary" size="large" onClick={onFinish}>
                    CANCEL
                  </Button>
                  <Button
                    disabled={!isValid || isSubmitting}
                    variant="contained"
                    color="secondary"
                    size="large"
                    type="submit"
                  >
                    SAVE
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
    </Stack>
  );
};

export default AddOrEditMenu;
